/**
 * FDM Trends Tab Component
 * Shows monthly trend summaries, by category and by flight phase
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCategory } from './fdmUtils';

export default function FDMTrendsTab({ 
  trendsData, 
  trendsByCategory,
  trendsByPhase
}) {
  return (
    <div className="space-y-6 mt-6">
      {/* Monthly Trend Summary */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg text-slate-100">Monthly Exceedance Rate</CardTitle>
          <CardDescription>Exceedances per 1000 flights over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {trendsData.slice(0, 6).map((trend, idx) => {
              const prevRate = trendsData[idx + 1]?.exceedances_per_1000_flights || 0;
              const currentRate = trend.exceedances_per_1000_flights || 0;
              const isUp = currentRate > prevRate;
              
              return (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-slate-700/30">
                  <div>
                    <p className="text-sm font-medium text-slate-300">
                      {new Date(trend.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-slate-500">{trend.total_flights} flights</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-semibold text-slate-200">{currentRate}</p>
                      <p className="text-xs text-slate-500">per 1000</p>
                    </div>
                    {idx < trendsData.length - 1 && (
                      <div className={`p-1 rounded ${isUp ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                        {isUp ? (
                          <ArrowUpRight className="w-4 h-4 text-red-400" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {trendsData.length === 0 && (
              <p className="text-slate-500 text-sm text-center py-4">No trend data available</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* By Category and By Phase */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">By Category</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trendsByCategory.slice(0, 8).map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between p-2">
                  <span className="text-sm text-slate-300">{formatCategory(cat.category)}</span>
                  <Badge variant="secondary">{cat.total}</Badge>
                </div>
              ))}
              {trendsByCategory.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg text-slate-100">By Flight Phase</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {trendsByPhase.slice(0, 8).map((phase, idx) => (
                <div key={idx} className="flex items-center justify-between p-2">
                  <span className="text-sm text-slate-300 capitalize">{phase.phase?.replace('_', ' ')}</span>
                  <Badge variant="secondary">{phase.count}</Badge>
                </div>
              ))}
              {trendsByPhase.length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">No data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
